import { useState } from 'react';
import { AgeGateOverlay } from '@hypneuronerd/age-gate';
import { storage } from '@hypneuronerd/storage';
import { RelatedApps, RELATED_BY_APP } from '@hypneuronerd/ui';
import { ReturnLink } from './components/ReturnLink';
import { MemoryPairs } from './components/MemoryPairs';
import { ReverseMode } from './components/ReverseMode';
import { GlossaryBrowser } from './components/GlossaryBrowser';
import styles from './App.module.css';

type View = 'welcome' | 'memory' | 'reverse' | 'browser' | 'memory-done' | 'reverse-done';
type Difficulty = 'starter' | 'standard' | 'deep';

interface Stats {
  memoryBestTime: number | null;
  memoryGames: number;
  reverseBestScore: number | null;
  reverseGames: number;
  /** Persisted difficulty preference, defaults to standard. */
  difficulty: Difficulty;
}

const STATS_KEY = 'glossary-match:stats';
const DEFAULT_STATS: Stats = {
  memoryBestTime: null,
  memoryGames: 0,
  reverseBestScore: null,
  reverseGames: 0,
  difficulty: 'standard',
};

// Difficulty → pair/question count. Starter is forgiving and short;
// deep is enough volume to actually stress recall.
const DIFFICULTY_CONFIG: Record<
  Difficulty,
  { memoryPairs: number; reverseQuestions: number; label: string; sub: string }
> = {
  starter: { memoryPairs: 4, reverseQuestions: 5, label: 'starter', sub: 'few cards, quick rounds' },
  standard: { memoryPairs: 6, reverseQuestions: 8, label: 'standard', sub: 'the default rhythm' },
  deep: { memoryPairs: 10, reverseQuestions: 14, label: 'deep', sub: 'enough to actually test you' },
};

export function App() {
  const [view, setView] = useState<View>('welcome');
  const [stats, setStats] = useState<Stats>(() => {
    // Stored stats may predate the difficulty field — fill it in
    // gracefully so existing visitors don't see a broken UI.
    const stored = storage.get<Partial<Stats>>(STATS_KEY, {});
    return { ...DEFAULT_STATS, ...stored };
  });
  const [lastResult, setLastResult] = useState<string>('');

  function updateStats(next: Stats) {
    setStats(next);
    storage.set(STATS_KEY, next);
  }

  function setDifficulty(difficulty: Difficulty) {
    updateStats({ ...stats, difficulty });
  }

  const config = DIFFICULTY_CONFIG[stats.difficulty];

  return (
    <>
      <AgeGateOverlay />
      <header className={styles.topNav}>
        <ReturnLink />
        {view !== 'welcome' && view !== 'memory-done' && view !== 'reverse-done' && (
          <button type="button" className={styles.backHome} onClick={() => setView('welcome')}>
            ← back to modes
          </button>
        )}
      </header>

      <main className={styles.stage}>
        {view === 'welcome' && (
          <section className={styles.welcome}>
            <h1 className={styles.heading}>glossary match</h1>
            <p className={styles.subtitle}>a small game of words</p>
            <p className={styles.body}>
              two ways to play, plus a quiet browser if you'd rather just read.
            </p>

            {/* Difficulty selector. The choice persists across sessions so
             * returning visitors don't have to re-pick. */}
            <div className={styles.difficulty}>
              <p className={styles.difficultyLabel}>round size</p>
              <div className={styles.difficultyRow}>
                {(Object.keys(DIFFICULTY_CONFIG) as Difficulty[]).map((d) => {
                  const isActive = stats.difficulty === d;
                  const cfg = DIFFICULTY_CONFIG[d];
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setDifficulty(d)}
                      className={`${styles.difficultyPill} ${isActive ? styles.difficultyPillActive : ''}`}
                      aria-pressed={isActive}
                    >
                      <span className={styles.difficultyPillLabel}>{cfg.label}</span>
                      <span className={styles.difficultyPillSub}>{cfg.sub}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className={styles.modes}>
              <button
                type="button"
                className={styles.modeCard}
                onClick={() => setView('memory')}
              >
                <span className={styles.modeName}>memory pairs</span>
                <span className={styles.modeBlurb}>flip cards to match terms with definitions</span>
                {stats.memoryBestTime !== null && (
                  <span className={styles.modeBest}>best: {stats.memoryBestTime}s</span>
                )}
              </button>

              <button
                type="button"
                className={styles.modeCard}
                onClick={() => setView('reverse')}
              >
                <span className={styles.modeName}>reverse</span>
                <span className={styles.modeBlurb}>given a term or definition, type the other</span>
                {stats.reverseBestScore !== null && (
                  <span className={styles.modeBest}>best: {stats.reverseBestScore}/8</span>
                )}
              </button>

              <button
                type="button"
                className={`${styles.modeCard} ${styles.modeBrowser}`}
                onClick={() => setView('browser')}
              >
                <span className={styles.modeName}>browse the glossary</span>
                <span className={styles.modeBlurb}>just the words, by category, no game</span>
              </button>
            </div>
          </section>
        )}

        {view === 'memory' && (
          <MemoryPairs
            pairs={config.memoryPairs}
            onDone={({ time, mismatches }) => {
              const next: Stats = {
                ...stats,
                memoryGames: stats.memoryGames + 1,
                memoryBestTime:
                  stats.memoryBestTime === null || time < stats.memoryBestTime
                    ? time
                    : stats.memoryBestTime,
              };
              updateStats(next);
              setLastResult(`${time}s with ${mismatches} misses`);
              setView('memory-done');
            }}
            onExit={() => setView('welcome')}
          />
        )}

        {view === 'reverse' && (
          <ReverseMode
            rounds={config.reverseQuestions}
            onDone={({ correct, total }) => {
              const next: Stats = {
                ...stats,
                reverseGames: stats.reverseGames + 1,
                reverseBestScore:
                  stats.reverseBestScore === null || correct > stats.reverseBestScore
                    ? correct
                    : stats.reverseBestScore,
              };
              updateStats(next);
              setLastResult(`${correct} of ${total} correct`);
              setView('reverse-done');
            }}
            onExit={() => setView('welcome')}
          />
        )}

        {view === 'browser' && <GlossaryBrowser onExit={() => setView('welcome')} />}

        {(view === 'memory-done' || view === 'reverse-done') && (
          <section className={styles.doneCard}>
            <p className={styles.doneLabel}>round complete</p>
            <p className={styles.doneResult}>{lastResult}</p>
            <div className={styles.doneActions}>
              <button
                type="button"
                className={styles.primary}
                onClick={() => setView(view === 'memory-done' ? 'memory' : 'reverse')}
              >
                play again
              </button>
              <button
                type="button"
                className={styles.secondary}
                onClick={() => setView('welcome')}
              >
                back to modes
              </button>
            </div>

            <RelatedApps apps={RELATED_BY_APP['glossary-match']} surface="midnight" />
          </section>
        )}
      </main>

      <footer className={styles.footer}>
        <p>no leaderboard, no streaks. just the words.</p>
      </footer>
    </>
  );
}
